import { request, type CapabilityEntry } from "../lib/api";
import type { PanelField, PanelAction } from "../panel/spec";
import type { CheckList, ChoiceMap } from "../panel/form";
import type { SettingsDeclaration } from "./directory";

interface ConfigField {
  key: string;
  title?: string;
  description: string;
  type?: string;
  choices?: unknown[];
  choice_labels?: Record<string, string>;
  advanced?: boolean;
  required?: boolean;
  secret?: boolean;
  /** A column of a table that names each row for good; a row's credentials are stored under it. */
  row_id?: boolean;
  items?: ConfigField[] | null;
}
export interface SecretStatus {
  name: string;
  title?: string;
  advanced?: boolean;
  description: string;
  required: boolean;
  prefix: string;
  present: boolean;
  names: string[];
}
export interface ConnectionSettings {
  namespace: string;
  panel_key: string;
  title: string;
  fields: PanelField[];
  values: Record<string, unknown>;
  /** The settings somebody stored, by name; null when the runtime did not say. */
  written?: string[] | null;
  generation: string | number | null;
  schema: ConfigField[] | null;
  secrets: SecretStatus[] | null;
  missing_config: string[] | null;
  missing_secrets: string[] | null;
  validation: "not_checked";
  actions: PanelAction[];
}
export interface Connections {
  connections: ConnectionSettings[];
  problems: { namespace: string; detail: string }[];
}
/** Declared entry points only: no saved values, credentials or dynamic options. */
export interface ConnectionDirectoryEntry {
  namespace: string;
  panel_key: string;
  title: string;
  generation: string | number | null;
  settings: SettingsDeclaration | null;
  state?: { kind: "connected" | "available" | "unknown"; label: string };
  activation?: "installed" | "failed" | "leaving" | null;
  search_fields?: { label: string; path: string[] }[];
}
export interface ConnectionDirectory {
  plugin_states?: Record<string,string>;
  entries: ConnectionDirectoryEntry[];
  connected: boolean;
}
export const connections = {
  list: () => request<Connections>("/api/connections"),
  directory: (signal?: AbortSignal) => request<ConnectionDirectory>("/api/connections?view=directory", { signal }),
  one: (namespace: string, signal?: AbortSignal) =>
    request<ConnectionSettings>(
      `/api/connections/${encodeURIComponent(namespace)}`, { signal },
    ),
  secret: (namespace: string, name: string, value: string) =>
    request<{ stored: boolean }>("/api/secrets", {
      method: "POST",
      body: JSON.stringify({ namespace, name, value }),
    }),
  /**
   * The owner's save: a direct write to the namespace (ruling 204).
   *
   * Not a turn and not a tool call, so nothing to follow: the answer is the
   * outcome. `generation` is what the values were read at; a save against an
   * older reading is refused rather than overwriting an edit this page never
   * saw.
   */
  write: (namespace: string, values: Record<string, unknown>, generation: unknown) =>
    request<{ namespace: string; keys: string[]; generation: number }>(
      `/api/connections/${encodeURIComponent(namespace)}`,
      { method: "POST", body: JSON.stringify({ values, generation }) },
    ),
  /**
   * Settings back to their declared defaults, or saved keys cleared (ruling
   * 2026-09-30). The same direct write as `write`; `keys` are the names that
   * held something.
   */
  reset: (namespace: string, keys: string[], generation: unknown) =>
    request<{ namespace: string; keys: string[]; generation: number }>(
      `/api/connections/${encodeURIComponent(namespace)}`,
      { method: "POST", body: JSON.stringify({ reset: keys, generation }) },
    ),
};

/** Secret fields never enter ordinary action values, including nested rows. */
export function ordinaryForm(
  fields: PanelField[],
  values: Record<string, unknown>,
): { fields: PanelField[]; values: Record<string, unknown> } {
  const cleanFields: PanelField[] = [];
  const cleanValues: Record<string, unknown> = {};
  for (const field of fields) {
    if (field.secret) continue;
    const saved = values[field.key];
    if (field.item_fields?.length) {
      const nested = ordinaryForm(field.item_fields, {});
      cleanFields.push({ ...field, item_fields: nested.fields });
      if (Array.isArray(saved))
        cleanValues[field.key] = saved.map(
          (row) =>
            ordinaryForm(
              field.item_fields!,
              row && typeof row === "object"
                ? (row as Record<string, unknown>)
                : {},
            ).values,
        );
    } else {
      cleanFields.push(field);
      if (saved !== undefined) cleanValues[field.key] = saved;
    }
  }
  return { fields: cleanFields, values: cleanValues };
}

export function changedFormValues(
  fields: PanelField[],
  values: Record<string, unknown>,
  baseline: Record<string, unknown>,
) {
  const output: Record<string, unknown> = {};
  for (const field of fields) {
    if (!(field.key in values)) continue;
    const fallback =
      field.type === "list"
        ? []
        : (field.default ??
          (field.type === "boolean" ? (field.required ? false : undefined) : ""));
    const previous = baseline[field.key] ?? fallback;
    if (JSON.stringify(previous) !== JSON.stringify(values[field.key]))
      output[field.key] = values[field.key];
  }
  return output;
}

/** A credential of one row of a table: named by the row's id, and `advanced` when the plugin says it is rarely needed. */
export interface NestedSecret {
  name: string;
  label: string;
  description: string;
  present: boolean | null;
  advanced: boolean;
}

export function nestedSecrets(connection: ConnectionSettings): NestedSecret[] {
  const result: NestedSecret[] = [];
  for (const field of connection.schema ?? []) {
    const rows = connection.values[field.key];
    if (!Array.isArray(rows)) continue;
    const idKey = field.items?.find((item) => item.row_id)?.key;
    rows.forEach((row, index) => {
      // A credential is stored under the row's fixed id, so a row that has none yet (not stored) has no box.
      const rowId = idKey ? row?.[idKey] : undefined;
      if (typeof rowId !== "string" || !rowId) return;
      for (const item of field.items ?? []) {
        if (!item.secret) continue;
        const marker = row?.[item.key];
        result.push({
          name: `${field.key}[${rowId}].${item.key}`,
          label: `${field.title || field.key} · 第 ${index + 1} 项 · ${item.title || item.key}`,
          description: item.description || "",
          present:
            marker && typeof marker.present === "boolean"
              ? marker.present
              : null,
          advanced: Boolean(item.advanced),
        });
      }
    });
  }
  return result;
}

/** A name a person can pick a capability by. */
export interface NamedKey { key: string; label: string }

/** What a model is called when the plugin that registered it gave it no name. */
export const NAMELESS_MODEL = "未命名的模型";

/**
 * What the owner reads each model as, by key: the name the plugin that
 * registered it gave it (the row's `title`), and never the key. Two models
 * under one name are told apart by their order, in words; the plugin that
 * named them is where they ought to differ, and this is only what is left
 * when they do not.
 */
export function modelNames(models: CapabilityEntry[]): Map<string, string> {
  const names = models.map(model => {
    const actual = (model.spec?.metadata as Record<string, unknown> | undefined)?.model_id;
    return typeof actual === "string" && actual.trim() ? actual.trim() : model.title?.trim() || NAMELESS_MODEL;
  });
  const total = new Map<string, number>();
  for (const name of names) total.set(name, (total.get(name) ?? 0) + 1);
  const seen = new Map<string, number>();
  return new Map(models.map((model, index) => {
    const name = names[index];
    if ((total.get(name) ?? 0) < 2) return [model.key, name];
    const nth = (seen.get(name) ?? 0) + 1;
    seen.set(name, nth);
    return [model.key, `${name}（第 ${nth} 个）`];
  }));
}

/**
 * What a settings page names a declared reference's values by, read once per
 * page from the registries: capabilities by what they do, models by the names
 * `modelNames` gives. A choice, a table and a checked list all pick from here.
 */
export interface Registries {
  capabilities: NamedKey[];
  models: NamedKey[];
  /** They were read. Unread, an empty list says nothing about what is connected. */
  read: boolean;
}

/** A setting that named no registry, or one whose registries could not be read. */
export const NO_REGISTRIES: Registries = { capabilities: [], models: [], read: false };

/** An unchosen reference, where the plugin that resolves it said nothing about it. */
export const UNCHOSEN = "不选";

/** What the plugin declared about one field under `settings.review`, if anything. */
function reviewOf(review: Record<string, unknown> | undefined, key: string): Record<string, unknown> | null {
  const said = review?.[key];
  return said && typeof said === "object" && !Array.isArray(said) ? said as Record<string, unknown> : null;
}

/** The names a declared `reference` picks among, or null for one that is not a registry. */
function referenced(said: Record<string, unknown>, registries: Registries): NamedKey[] | null {
  if (said.reference === "model") return registries.models;
  if (said.reference === "tool") return registries.capabilities;
  return null;
}

function words(value: unknown): string {
  return typeof value === "string" ? value.trim() : "";
}

/**
 * The fields a plugin said hold a model (`settings.review.<key>.reference:
 * "model"`), as a choice among the models connected now, each by its name.
 *
 * The stored value is a registry key, which a person never reads; without the
 * declaration the page could only show it in a text box. A value that is no
 * longer connected stays what it is, and the form says so.
 *
 * Choosing none is an answer too, unless the field must be set: what it means
 * is the plugin's to say, since that plugin resolves it -- its
 * `settings.when_unset.<key>.label` -- and the shell's 「不选」 where it said
 * nothing.
 */
export function modelFields(
  fields: PanelField[],
  declared: SettingsDeclaration,
  registries: Registries,
): PanelField[] {
  return fields.map((field) => {
    const said = reviewOf(declared.review, field.key);
    const models = field.type === "string" && said?.reference === "model" ? referenced(said, registries) : null;
    if (!models) return field;
    const unchosen = field.required ? []
      : [{ key: "", label: words(declared.when_unset?.[field.key]?.label) || UNCHOSEN }];
    const offered = [...unchosen, ...models];
    return {
      ...field,
      type: "choice",
      choices: offered.map((model) => model.key),
      choice_labels: Object.fromEntries(offered.map((model) => [model.key, model.label])),
    };
  });
}

function labels(value: unknown): NamedKey[] {
  if (!value || typeof value !== "object" || Array.isArray(value)) return [];
  return Object.entries(value as Record<string, unknown>)
    .filter((entry): entry is [string, string] => typeof entry[1] === "string" && Boolean(entry[1].trim()))
    .map(([key, label]) => ({ key, label }));
}

/**
 * Which declared lists are a set of named values, drawn as checkboxes.
 *
 * The plugin said so, for one of its plain `array` fields: `value_labels`
 * names every value it offers, or `reference: "model"` says the values are
 * models, and the connected ones are offered by name. `ordered: true` is the
 * plugin saying its code reads the list in order. Capabilities are not offered
 * this way: only the ones with an owner's name are on the list, and a stored
 * one without would read as withdrawn when it is not. A stored value that is not
 * a list of words keeps its rows, because checkboxes would hide it.
 */
export function checkLists(
  schema: ConfigField[],
  values: Record<string, unknown>,
  review: Record<string, unknown> | undefined,
  registries: Registries,
): Record<string, CheckList> {
  const output: Record<string, CheckList> = {};
  for (const field of schema) {
    const said = reviewOf(review, field.key);
    if (field.type !== "array" || field.items || field.secret || !said) continue;
    // Unread, the models would all be drawn as withdrawn, each with a button
    // to remove it; the plain rows keep them as they are instead.
    if (said.reference === "model" && !registries.read) continue;
    const options = said.reference === "model" ? referenced(said, registries) : labels(said.value_labels);
    if (!options || (!options.length && said.reference !== "model")) continue;
    const stored = values[field.key];
    if (stored != null && (!Array.isArray(stored) || stored.some((item) => typeof item !== "string"))) continue;
    output[field.key] = {
      options: options.map(({ key, label }) => ({ value: key, label })),
      ordered: said.ordered === true,
    };
  }
  return output;
}

/**
 * Which declared `object` settings are a table of choices, and the words for them.
 *
 * None of this is the shell's reading of a setting. A plugin that declared, for
 * one of its object fields, what each answer is called (`settings.review.<key>.
 * value_labels`) has said the field is a table of those answers. `key_labels`
 * names the rows that are always there; a `reference` to a registry says the
 * names are capabilities or models, which a person picks by name. A field with
 * answers and neither of those stays the JSON box, because the shell has no
 * names to offer -- and so does a stored value that is not such a table,
 * because a column of selects would hide it.
 */
export function choiceMaps(
  schema: ConfigField[],
  values: Record<string, unknown>,
  review: Record<string, unknown> | undefined,
  registries: Registries,
): Record<string, ChoiceMap> {
  const output: Record<string, ChoiceMap> = {};
  for (const field of schema) {
    const said = reviewOf(review, field.key);
    if (field.type !== "object" || field.secret || !said) continue;
    const choices = labels(said.value_labels).map(({ key, label }) => ({ value: key, label }));
    const rows = labels(said.key_labels);
    const options = referenced(said, registries);
    if (!choices.length || (!rows.length && !options)) continue;
    const stored = values[field.key];
    if (stored != null && (typeof stored !== "object" || Array.isArray(stored) ||
      Object.values(stored as Record<string, unknown>).some((answer) => typeof answer !== "string"))) continue;
    output[field.key] = { choices, rows, ...(options ? { options } : {}) };
  }
  return output;
}

/**
 * Structured rows stay structured; only a declared object uses JSON text --
 * except the ones in `tables`, which the page draws as rows and which therefore
 * stay the object they are in both directions.
 */
export function configurationValues(
  schema: ConfigField[],
  values: Record<string, unknown>,
  direction: "display" | "submit",
  tables?: ReadonlySet<string>,
): Record<string, unknown> {
  const output = { ...values };
  for (const field of schema) {
    const value = output[field.key];
    if (direction === "submit" && value === "" && !field.required && (
      field.type === "number" || field.type === "integer" ||
      (field.choices?.length && !field.choices.includes(""))
    )) {
      delete output[field.key];
      continue;
    }
    if (Array.isArray(value) && field.items) {
      output[field.key] = value.map((row) =>
        configurationValues(
          field.items!,
          row as Record<string, unknown>,
          direction,
        ),
      );
    } else if (field.type === "object") {
      if (tables?.has(field.key) && typeof value !== "string") continue;
      if (direction === "display" && value && typeof value === "object")
        output[field.key] = JSON.stringify(value, null, 2);
      if (direction === "submit" && typeof value === "string") {
        if (!value.trim() && !field.required) {
          delete output[field.key];
          continue;
        }
        let object: unknown;
        try {
          object = JSON.parse(value);
        } catch {
          throw new Error(`${field.title || field.key} 需要有效的 JSON 对象。`);
        }
        if (!object || typeof object !== "object" || Array.isArray(object))
          throw new Error(`${field.title || field.key} 需要 JSON 对象，不能是列表或文字。`);
        output[field.key] = object;
      }
    }
  }
  return output;
}
